import { useLatest } from 'ahooks';
import { useEffect, useRef } from 'react';
import { ITouchKitEvent, ITouchKitEventCallback } from '@/utils/event-kit';
import { useIdentity } from '../core';
import { InstanceData, InstanceDataType } from '../data';
import { useEditorOperateState } from './states/editor-operate-state';
import { useLayerOperateState } from './states/layer-operate-state';
import { MoveListenerLevel } from './type';

/** 获取悬浮图层 */
export const useHoverId = () => {
    return useLayerOperateState()((state) => state.hoverId);
};

/** 获取激活图层 */
export const useActiveInfoKey = () => {
    return useLayerOperateState()((state) => state.activeInfoKey);
};

/** 获取选中图层 */
export const useSelectedInfoKey = () => {
    return useLayerOperateState()((state) => state.selectedInfoKey);
};

/** 获取光标提示 */
export const useCursorTip = () => {
    return useEditorOperateState()((state) => state.cursorTipVer);
};

/** 获取全局遮罩参数 */
export const useGlobalMask = () => {
    return useEditorOperateState()((state) => state.globalMask);
};

/** 获取是否正在执行基础操作 */
export const useIsBasicOperate = () => {
    return useEditorOperateState()((state) => state.isBaseOperate);
};

/** 获取是否禁用拾取 */
export const useIsDisablePick = () => {
    return useEditorOperateState()((state) => state.disablePick);
};

/** 获取是否禁用操作 */
export const useIsDisableOperate = () => {
    return useEditorOperateState()((state) => state.disableOperate);
};

/** 获取是否正在执行鼠标框选 */
export const useIsMouseSelect = () => {
    return useEditorOperateState()((state) => state.isMouseSelect);
};

/** 移动事件监听 */
export const useMoveEventListener = (
    handler: (event: ITouchKitEvent) => void,
    /** level越大, 越先执行 */
    level = MoveListenerLevel.DEFAULT_LEVEL,
    name = '',
) => {
    const identity = useIdentity();
    // console.log('useMoveEventListener', identity);
    const startMoveEvent = useRef<ITouchKitEvent | null>(null);
    const realMoveCallbackCount = useRef(0);

    const listener = (event: ITouchKitEvent) => {
        if (event.isStart) {
            startMoveEvent.current = event;
            return;
        }
        if (event.isEnd) {
            if (realMoveCallbackCount.current > 0) {
                handler(event);
            }
            // reset
            startMoveEvent.current = null;
            realMoveCallbackCount.current = 0;
            return;
        }
        /**
         * 在Mac触控板上，即使手指没有移动，也有可能触发mousemove事件
         * 这可能是由于触控板的精确度和敏感度造成的。当手指接触触控板时，即使微微晃动或触摸压力发生微小变化，也会被视为移动。
         * 所以需要过滤掉
         */
        if (startMoveEvent.current && startMoveEvent.current.x === event.x && startMoveEvent.current.y === event.y)
            return;

        if (realMoveCallbackCount.current === 0 && startMoveEvent.current) {
            handler(startMoveEvent.current);
        }

        handler(event);
        realMoveCallbackCount.current++;
    };

    const listenerRef = useLatest(listener);

    useEffect(() => {
        const tkListenerList = InstanceData.getData(identity, InstanceDataType.TouchListenerList) as {
            name: string;
            level: MoveListenerLevel;
            listenerRef: { current: ITouchKitEventCallback };
        }[];
        const listenerHandle = `${name}-${identity}`;
        // 加入事件队列
        tkListenerList.push({
            name: listenerHandle,
            level,
            listenerRef,
        });

        // 移除事件队列
        return () => {
            const index = tkListenerList.findIndex((item) => item.listenerRef === listenerRef);
            if (index !== -1) {
                tkListenerList.splice(index, 1);
            }
        };
    }, []);
};
